/**
 * Text on Clean redraw / Line map: every extracted text item is drawn where the drawing
 * has it and at its size, so it zooms with the sheet and never moves. An identifier the
 * drawing keeps only on its symbol goes at a fixed spot beside the symbol. Each item
 * carries its height on the sheet (data-h); the view shows it once it is big enough to read.
 */
import { type Box, type Model, PROC, catOf, isTagBubble, polyMid, procDeg, snap } from './model'
import { esc, r1 } from './glyphs'

const UI = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace'
/** the font each kind of text is drawn in (matches cleanRedraw.css), for measuring it */
const FONT: Record<string, string> = { label: `650 100px ${UI}`, name: `700 100px ${UI}`, line: `500 100px ${MONO}`, pill: `500 100px ${MONO}`, attr: `600 100px ${UI}`, note: `italic 500 100px ${UI}` }
/** text may be tightened this much to fit the drawing's box; past that it gets smaller instead */
const MAX_SQUEEZE = 0.85

let ctx: CanvasRenderingContext2D | null = null
const widthCache = new Map<string, number>()
export function textWidth(t: string, font: string): number {
  const k = font + '|' + t
  let v = widthCache.get(k)
  if (v === undefined) {
    ctx = ctx ?? (typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null)
    if (ctx) { ctx.font = font; v = ctx.measureText(t).width }
    if (!v) v = t.length * 0.6 * parseFloat(/(\d+(?:\.\d+)?)px/.exec(font)?.[1] ?? '10') // no canvas, or it measured nothing
    widthCache.set(k, v)
  }
  return v
}

/** a fill light enough that white text on it is hard to read */
function isLight(hex: string): boolean {
  const v = (i: number) => parseInt(hex.slice(i, i + 2), 16) / 255
  return hex.length === 7 && 0.2126 * v(1) + 0.7152 * v(3) + 0.0722 * v(5) > 0.55
}

export interface TextOptions {
  attrs: boolean
  notes: boolean
  /** is this line drawn (signal lines can be switched off) */
  lineDrawn: (c: number) => boolean
  /** Line map: the pill colour for a line number on line c (null: that line has none) */
  pill?: (c: number) => string | null
}

/** the usual height of text on this sheet: the size for identifiers the drawing doesn't place */
function typicalHeight(m: Model): number {
  const hs = m.TAGS.map(t => { const b = m.nodes[t].bb; return [b[2] - b[0], b[3] - b[1]] }).filter(([w, h]) => w > h && h > 0).map(([, h]) => h).sort((a, b) => a - b)
  return hs.length ? hs[Math.floor(hs.length * 0.4)] : 24 // below the median: multi-line boxes skew it upward
}

/** text filling box b as the drawing has it: rotated when the box runs vertically */
export function boxText(cls: string, dataI: number, b: Box, tx: string, back: '' | 'chip' | string = '', lineH = 0): string {
  const w = b[2] - b[0], h = b[3] - b[1], vert = h > w * 1.25 && tx.length >= 3
  if (!(w > 0 && h > 0)) return '' // a text box with no size can't be drawn where it is
  const along = vert ? h : w, across = vert ? w : h, cx = (b[0] + b[2]) / 2, cy = (b[1] + b[3]) / 2
  // the drawing's (often narrow CAD) lettering: keep its height, tighten at most MAX_SQUEEZE, else go smaller; never crush
  const kind = /st-name/.test(cls) ? 'name' : /st-(\w+)/.exec(cls)?.[1] ?? 'label', font = FONT[kind] ?? FONT.label
  // a box several text lines tall held several lines on the drawing (the parse joins them): wrap the words back
  const nLines = lineH > 0 && / /.test(tx) ? Math.max(1, Math.min(4, Math.round(across / (lineH * 1.15)))) : 1
  const lines = nLines > 1 ? wrap(tx, nLines, font) : [tx], slot = across / lines.length
  const fits = lines.map(l => {
    const per = textWidth(l, font) / 100
    let fs = slot * 0.82, len: number | null = null
    if (per * fs > along) { if (per * fs * MAX_SQUEEZE <= along) len = along; else { fs = along / (per * MAX_SQUEEZE); len = along } }
    return { l, fs, len }
  })
  const fs = Math.min(...fits.map(f => f.fs))
  const rot = vert ? ` transform="rotate(-90 ${r1(cx)} ${r1(cy)})"` : ''
  const p = across * 0.14, bx = vert ? [cx - along / 2 - p, cy - across / 2 - p, along + 2 * p, across + 2 * p] : [b[0] - p, b[1] - p, w + 2 * p, h + 2 * p]
  const rect = back === 'chip' ? `<rect class="chip" x="${r1(bx[0])}" y="${r1(bx[1])}" width="${r1(bx[2])}" height="${r1(bx[3])}" rx="${r1(across * 0.15)}"${rot}/>`
    : back ? `<rect class="pill" x="${r1(bx[0])}" y="${r1(bx[1])}" width="${r1(bx[2])}" height="${r1(bx[3])}" rx="${r1(Math.min(bx[2], bx[3]) / 2)}" style="fill:${back}"${rot}/>` : ''
  const ink = back && back !== 'chip' && isLight(back) ? ' fill:#17212b;' : ''
  const texts = fits.map((f, k) => {
    const fit = f.len && f.fs === fs ? ` textLength="${r1(f.len)}" lengthAdjust="spacingAndGlyphs"` : ''
    const y = cy - across / 2 + slot * (k + 0.5) + fs * 0.35
    return `<text x="${r1(cx - along / 2)}" y="${r1(y)}" style="font-size:${r1(fs)}px;${ink}"${fit}${rot}>${esc(f.l)}</text>`
  }).join('')
  return `<g class="${cls}" data-i="${dataI}" data-h="${r1(fs / 0.82)}">${rect}${texts}</g>`
}

/** words split into n lines of similar width */
function wrap(tx: string, n: number, font: string): string[] {
  const words = tx.split(/\s+/), target = textWidth(tx, font) / n, out: string[] = []
  let cur = ''
  for (const w of words) {
    const next = cur ? cur + ' ' + w : w
    if (cur && textWidth(next, font) > target * 1.08 && out.length < n - 1) { out.push(cur); cur = w } else cur = next
  }
  return [...out, cur]
}

/** a box of the sheet's usual text height for tx, centred vertically on y, starting at x (ending at x when leftward) */
function besideBox(tx: string, x: number, y: number, H: number, leftward: boolean): Box {
  const W = textWidth(tx, `600 ${H}px ${UI}`) * 1.02
  return leftward ? [x - W, y - H / 2, x, y + H / 2] : [x, y - H / 2, x + W, y + H / 2]
}

/** every text item of the drawing, as sheet-space SVG */
export function sheetText(m: Model, o: TextOptions, GB: Map<number, Box>): { svg: string; count: number } {
  const out: string[] = [], H = typicalHeight(m)
  // 1. extracted text, where the drawing has it
  for (const t of m.TAGS) {
    const n = m.nodes[t], tx = (n.tx ?? '').trim(); if (!tx) continue
    const own = m.tagOwner[t]
    if (own < 0) { if (o.notes) out.push(boxText('st st-note', t, n.bb, tx, '', H)); continue }
    const O = m.nodes[own]
    if (O.k === 's') {
      if (!GB.has(own)) continue
      const K = m.KIND[own], c = n.bb, cx = (c[0] + c[2]) / 2, cy = (c[1] + c[3]) / 2
      if ((K === 'inst' || isTagBubble(m, own)) && cx > O.bb[0] && cx < O.bb[2] && cy > O.bb[1] && cy < O.bb[3]) continue // the bubble's own lettering
      if (K === 'fit' && /insulation/.test(O.st ?? '')) continue // drawn inside the pill
      const named = K === 'equip' || K === 'conn' // their text is their name (the parse links it as an annotation)
      if (m.TAGREL.get(t) !== 'label' && !named) { if (o.attrs) out.push(boxText(`st st-attr cat-${catOf(m, own)}`, own, n.bb, tx, 'chip', H)); continue }
      out.push(boxText(`st st-label cat-${catOf(m, own)}${named ? ' st-name' : ''}`, own, n.bb, tx, '', H))
    } else {
      if (!o.lineDrawn(own)) continue
      if (o.pill) { const color = o.pill(own); if (color) out.push(boxText('st st-pill', own, n.bb, tx, color)) }
      else out.push(boxText('st st-line', own, n.bb, tx))
    }
  }
  // 2. a symbol's identifier the drawing keeps only on the symbol: beside it, at the sheet's usual text size
  for (const [i, gb] of GB) {
    const n = m.nodes[i], K = m.KIND[i], id = (n.tag ?? '').trim()
    if (!id || K === 'tee' || K === 'fit' || K === 'note' || K === 'inst' || isTagBubble(m, i)) continue
    if (m.foldTags[i].some(t => (m.nodes[t].tx ?? '').trim().toLowerCase() === id.toLowerCase())) continue
    const cls = `st st-label cat-${catOf(m, i)}${K === 'equip' || K === 'conn' ? ' st-name' : ''}`
    if ((K === 'valve' || K === 'cv' || K === 'fe') && procDeg(m, i) > 0 && snap(m, i).hz) { // above its horizontal pipe, centred
      const W = textWidth(id, `650 ${H}px ${UI}`) * 1.02, cx = (gb[0] + gb[2]) / 2
      out.push(boxText(cls, i, [cx - W / 2, gb[1] - H * 1.25, cx + W / 2, gb[1] - H * 0.25], id)); continue
    }
    const leftward = K === 'conn' && (gb[0] + gb[2]) / 2 > m.W / 2
    out.push(boxText(cls, i, besideBox(id, leftward ? gb[0] - H * 0.3 : gb[2] + H * 0.3, (gb[1] + gb[3]) / 2, H, leftward), id))
  }
  // 3. a line number the drawing has no text for (known only on the line): once, on its longest stretch
  const written = new Set(m.CONNS.flatMap(c => m.pipeTags[c].map(t => (m.nodes[t].tx ?? '').trim())))
  const longest = new Map<string, { c: number; len: number }>()
  for (const c of m.CONNS) {
    const n = m.nodes[c]; if (!n.no || written.has(n.no) || !PROC.has(n.lk!) || !o.lineDrawn(c)) continue
    const len = polyMid(n.ln!)?.len ?? 0, cur = longest.get(n.no)
    if (!cur || len > cur.len) longest.set(n.no, { c, len })
  }
  for (const [no, { c }] of longest) {
    const color = o.pill ? o.pill(c) : null
    if (o.pill && !color) continue
    const mm = polyMid(m.nodes[c].ln!); if (!mm) continue
    const W = textWidth(no, `500 ${H}px ui-monospace, monospace`), vert = Math.abs(mm.dy) > Math.abs(mm.dx), g = H * 0.4
    const b: Box = vert ? [mm.x + g, mm.y - W / 2, mm.x + g + H, mm.y + W / 2] : [mm.x - W / 2, mm.y - g - H, mm.x + W / 2, mm.y - g]
    out.push(color ? boxText('st st-pill', c, b, no, color) : boxText('st st-line', c, b, no))
  }
  return { svg: `<g class="st-layer">${out.join('')}</g>`, count: out.length }
}
